import React, { useEffect, useState } from 'react';
import { Clock, Truck, Package, AlertTriangle, Info, TrendingUp, TrendingDown } from 'lucide-react';
import { kpis, mockAlerts } from '../data/mockData';
interface TopBarProps {
  onFilterAlerts?: (severity: 'critical' | 'warning' | 'low' | 'all') => void;
}
export function TopBar({
  onFilterAlerts
}: TopBarProps) {
  const criticalCount = mockAlerts.filter(a => a.severity === 'critical').length;
  const warningCount = mockAlerts.filter(a => a.severity === 'warning').length;
  const lowCount = mockAlerts.filter(a => a.severity === 'info' || a.severity === 'offline').length;
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setElapsedSeconds(s => s + 1), 1000);
    return () => clearInterval(timer);
  }, []);
  const startMinutes = 13 * 60 + 3;
  const totalMinutes = startMinutes + Math.floor(elapsedSeconds / 60);
  const hours = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  const timeString = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  return <div className="h-16 bg-slate-950 border-b border-slate-800 flex items-center justify-between px-6 shrink-0 z-10" data-id="element-377">
      {/* Left: Active Alerts */}
      <div className="flex items-center gap-6" data-id="element-378">
        <div className="flex items-center gap-2" data-id="element-379">
          <button onClick={() => onFilterAlerts?.('critical')} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-brand-red/10 border border-brand-red/20 hover:bg-brand-red/20 hover:border-brand-red/40 transition-all cursor-pointer" data-id="element-380">
            <AlertTriangle className="w-4 h-4 text-brand-red" data-id="element-381" />
            <span className="text-xl font-bold font-mono text-brand-red" data-id="element-382">
              {criticalCount}
            </span>
          </button>
          <button onClick={() => onFilterAlerts?.('warning')} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-brand-amber/10 border border-brand-amber/20 hover:bg-brand-amber/20 hover:border-brand-amber/40 transition-all cursor-pointer" data-id="element-383">
            <AlertTriangle className="w-4 h-4 text-brand-amber" data-id="element-384" />
            <span className="text-xl font-bold font-mono text-brand-amber" data-id="element-385">
              {warningCount}
            </span>
          </button>
          <button onClick={() => onFilterAlerts?.('low')} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-brand-blue/10 border border-brand-blue/20 hover:bg-brand-blue/20 hover:border-brand-blue/40 transition-all cursor-pointer" data-id="element-386">
            <Info className="w-4 h-4 text-brand-blue" data-id="element-387" />
            <span className="text-xl font-bold font-mono text-brand-blue" data-id="element-388">
              {lowCount}
            </span>
          </button>
        </div>
      </div>

      {/* Center: Digital Clock */}
      <div className="absolute left-1/2 -translate-x-1/2" data-id="element-389">
        <span className="text-[28px] block font-light tracking-widest leading-none border border-slate-850" style={{
        fontFamily: "'Orbitron', sans-serif",
        color: '#6b7280',
        padding: '5px',
        backgroundColor: '#070c17',
        borderRadius: '4px'
      }} data-id="element-390">
          {timeString}
        </span>
      </div>

      {/* Right: KPIs */}
      <div className="flex items-center gap-6" data-id="element-391">
        <KPICard icon={<Truck className="w-3.5 h-3.5 text-slate-400" data-id="element-393" />} label="Active Drivers" value={`${kpis.activeDrivers.current}/${kpis.activeDrivers.total}`} data-id="element-392" />

        <div className="w-px h-8 bg-slate-800" data-id="element-394" />

        <KPICard icon={<Package className="w-3.5 h-3.5 text-slate-400" data-id="element-396" />} label="Deliveries" value={`${kpis.deliveries.current}/${kpis.deliveries.total}`} data-id="element-395" />

        <div className="w-px h-8 bg-slate-800" data-id="element-397" />

        <KPICard icon={<Clock className="w-3.5 h-3.5 text-slate-400" data-id="element-399" />} label="On-Time Rate" value={`${kpis.onTimeRate}%`} trend="down" trendValue="-1.2%" data-id="element-398" />
      </div>
    </div>;
}
function KPICard({
  icon,
  label,
  value,
  trend,
  trendValue,
  valueColor = 'text-slate-100'
}: any) {
  return <div className="flex flex-col" data-id="element-400">
      <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-1" data-id="element-401">
        {icon}
        <span data-id="element-402">{label}</span>
      </div>
      <div className="flex items-baseline gap-2" data-id="element-403">
        <span className={`text-lg font-semibold font-mono ${valueColor}`} data-id="element-404">
          {value}
        </span>
        {trend && <span className={`flex items-center text-[10px] font-medium ${trend === 'up' ? 'text-brand-green' : 'text-brand-red'}`} data-id="element-405">
            {trend === 'up' ? <TrendingUp className="w-3 h-3 mr-0.5" data-id="element-406" /> : <TrendingDown className="w-3 h-3 mr-0.5" data-id="element-407" />}
            {trendValue}
          </span>}
      </div>
    </div>;
}